import { post, postJSON, get } from "api/base";
import { isServer } from "util/browser-utils";
import Persistence from "pages/common/lib/grabyo-persistence";
import { clientRedirectToLogin } from "pages/common/state/utils";
import { HeapAnalytics } from "page/analytics";

export const getOrFailGracefully = async (
    endpoint: string,
    defaultResponse: unknown
) => {
    return async () => {
        try {
            const { data } = await get(endpoint);
            return data;
        } catch (e) {
            return {
                data: defaultResponse
            };
        }
    };
};

export const CAMPAIGNS_ENDPOINT = `/ws/campaigns`;

export const GRAB_THUMBNAIL = `/ws/pipeline/thumbnail`;

export const getCampaigns = async (pageSize = 20) => {
    try {
        const response = await get(
            `${CAMPAIGNS_ENDPOINT}?pageSize=${pageSize}`
        );
        return response;
    } catch (err) {
        return {
            data: []
        };
    }
};

export const getStreams = async () => {
    const { data } = await get(`/v1/streams`);
    return data;
};

export const getUsers = async () => {
    const { data } = await get(`/v1/users`);
    return data;
};

export const getLoggedInUser = async () => {
    const {
        data: { data }
    } = await get(`v1/account`);
    return data;
};

export const getCustomRtmpCredentials = async () => {
    try {
        const {
            data: { data }
        } = await get(`v1/account/custom-rtmp-credentials`);
        return data;
    } catch (e) {
        return [];
    }
};

export const getOrganization = async () => get(`/ws/organizations/me`);

/**
 * @summary - Log in to Grabyo Studio
 *
 * @param email - The user's email acddress
 * @param password - the user's raw password
 * @param mfaCode - The user's MFA code if they have MFA enabled
 *
 * @return See Studio API docs
 */
export const login = async (
    email: string,
    password: string,
    mfaCode?: number
) =>
    postJSON(`v2/login`, {
        email,
        password,
        ...(mfaCode && { mfaCode })
    });

export const logoutUser = async () => {
    await postJSON(`v2/logout`);
    Persistence.PermStore.clear();
};

export const logout = async (isHttpError: boolean) => {
    await logoutUser();
    HeapAnalytics.resetIdentity();
    if (!isServer()) {
        clientRedirectToLogin(isHttpError);
    }
};

export const forgotPassword = async (email: string) =>
    postJSON(`v1/account/reset-password/init`, {
        email
    });

export const resetPassword = async (resetCode: string, password: string) =>
    postJSON(`v2/account/reset-password/finish`, {
        tokenId: resetCode,
        password
    });

export const acceptInvite = async (
    userId: string,
    resetCode: string,
    password: string
) =>
    postJSON(`v2/account/accept-invite`, {
        userId,
        tokenId: resetCode,
        password
    });

export const getShare = async (id: string) => {
    const { data } = await get(`ws/shares/${id}`);
    return data;
};

export const getGrab = async (
    id: string,
    extended = false,
    noCache = false
) => {
    const { data } = await get(`ws/grabs/${id}`, {
        params: { extended, noCache }
    });
    return data;
};

export const setGrabThumbnailByUrl = (shareId: string, url: string) => {
    return post(GRAB_THUMBNAIL, { shareId, thumbnail: url });
};

export const setGrabThumbnailByTime = (shareId: string, time: number) => {
    return post(GRAB_THUMBNAIL, { shareId, thumbnailTime: time });
};
